<template>
    <Head :title="t('assessments.rater.relationship.pageTitle')" />
    <RaterChrome :show-anonymous="!invite?.is_authenticated">
        <div v-if="loading" class="body-small text-ink-muted">{{ t("assessments.rater.relationship.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <template v-else-if="subjectFirstName && responseId">
            <div class="relationship-page">
                <BaseLink :href="`/reflections/360/r/${props.invite_token}/`" class="back-link">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i>
                    {{ t("assessments.rater.relationship.back") }}
                </BaseLink>

                <header class="header">
                    <h1 class="display-serif text-ink leading-tight">{{ t("assessments.rater.relationship.title", { name: subjectFirstName }) }}</h1>
                    <p class="body-small text-ink-muted">{{ t("assessments.rater.relationship.subtitle", { name: subjectFirstName }) }}</p>
                </header>

                <HorizontalDivider as="hr" spacing="none" />

                <div class="options" role="radiogroup" :aria-label="t('assessments.rater.relationship.optionsLabel')">
                    <button
                        v-for="opt in options"
                        :key="opt.id"
                        type="button"
                        role="radio"
                        :aria-checked="selected === opt.id"
                        class="option"
                        :class="{ 'option--selected': selected === opt.id }"
                        @click="selected = opt.id">
                        <div class="option__label">{{ opt.label }}</div>
                        <div class="option__body">{{ opt.body }}</div>
                    </button>
                </div>

                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

                <div class="actions">
                    <BaseLink :href="`/reflections/360/r/${props.invite_token}/`" class="back-text">{{ t("assessments.rater.relationship.back") }}</BaseLink>
                    <BaseButton type="button" theme="primary" class="inline-flex items-center gap-2" :disabled="!selected || submitting" @click="onContinue">
                        {{ t("assessments.rater.relationship.continue") }} <i class="bi bi-arrow-right" aria-hidden="true"></i>
                    </BaseButton>
                </div>
            </div>
        </template>
    </RaterChrome>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import RaterChrome from "~vue/components/RaterChrome.vue";
import { useInviteLoader } from "~vue/composables/useInviteLoader";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic } from "~vue/utils/sessionEnded";
import { DEFAULT_RATER_RELATIONSHIPS, isRelationshipRejected, relationshipOptionBody } from "~vue/utils/threeSixty";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface ResponsePayload {
    id: string;
    relationship: string | null;
}

interface Props {
    invite_token: string;
    is_anonymous: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();

// An external rater has no account to log back into, so a rejected token there must stay silent.
// An internal rater is signed in, and a real session expiry must still offer them recovery.
if (props.is_anonymous) {
    markPagePublic();
}

const {
    invite,
    loading: inviteLoading,
    loadError: inviteLoadError,
    reload: reloadInvite,
} = useInviteLoader(props.invite_token, {
    redirectIfResponded: `/reflections/360/r/${props.invite_token}/done`,
    redirectIfCycleClosed: `/reflections/360/r/${props.invite_token}/`,
    redirectIfRevoked: `/reflections/360/r/${props.invite_token}/`,
});

// The subject already categorized this rater at nomination — the relationship
// step doesn't apply, so a direct visit skips to the questions.
watch(invite, (loaded) => {
    if (loaded?.relationship_prefilled) {
        router.visit(`/reflections/360/r/${props.invite_token}/q/1`);
    }
});

const responseId = ref<string>("");
const selected = ref<string | null>(null);
const responseLoading = ref(true);
const responseLoadError = ref<string | null>(null);
const submitting = ref(false);
const error = ref<string | null>(null);

const subjectFirstName = computed(() => invite.value?.subject_first_name ?? "");
const loading = computed(() => inviteLoading.value || responseLoading.value);
const loadError = computed(() => inviteLoadError.value || responseLoadError.value);

const options = computed(() =>
    (invite.value?.relationships ?? DEFAULT_RATER_RELATIONSHIPS).map((option) => ({
        id: option.value,
        label: option.label,
        body: relationshipOptionBody(option.value, subjectFirstName.value),
    })),
);

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/invites/${props.invite_token}/response`).post().json();
    responseLoading.value = false;
    if (fetchError.value) {
        responseLoadError.value = (data.value as { error?: string })?.error || t("assessments.rater.relationship.errors.loadFailed");
        return;
    }
    const response = (data.value as { response: ResponsePayload }).response;
    responseId.value = response.id;
    if (response.relationship && response.relationship !== "self") {
        selected.value = response.relationship;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_RELATIONSHIP_VIEWED, {
        has_existing_selection: response.relationship !== null,
    });
});

async function onContinue() {
    if (!selected.value || submitting.value) {
        return;
    }
    submitting.value = true;
    error.value = null;
    try {
        const { error: fetchError, data } = await useFetch(`/v1/reflections/responses/${responseId.value}/relationship?invite_token=${props.invite_token}`)
            .post(JSON.stringify({ relationship: selected.value }), "application/json")
            .json();
        if (fetchError.value) {
            error.value = (data.value as { error?: string })?.error || t("assessments.rater.relationship.errors.saveFailed");
            // The organization dropped this category while the page was open. Re-read
            // the current ones and drop the now-invalid pick, so the rater can carry on
            // instead of every retry failing against a list they can't see.
            if (isRelationshipRejected(data.value)) {
                await reloadInvite();
                if (!options.value.some((opt) => opt.id === selected.value)) {
                    selected.value = null;
                }
            }
            return;
        }
        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_RELATIONSHIP_SUBMITTED, {
            relationship: selected.value,
        });
        router.visit(`/reflections/360/r/${props.invite_token}/q/1`);
    } finally {
        submitting.value = false;
    }
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.relationship-page {
    @apply flex flex-col gap-6;
    /* Landing → relationship → question is one continuous green run; this step
       used to break it with olive. Green is the ledger's team-reflect family
       and its done status, so both ride the same status token. */
    --relationship-accent: var(--color-status-success);
    --relationship-accent-surface: var(--color-status-success-soft);
}

.back-link {
    @apply inline-flex items-center gap-1 self-start body-small text-ink-muted hover:text-[color:var(--relationship-accent)] transition-colors;
}

.header {
    @apply flex flex-col gap-3;
}

.options {
    @apply flex flex-col gap-3;
}

.option {
    @apply w-full text-left rounded-lg border border-border-strong bg-surface-raised px-5 py-4 transition-colors;
    @apply hover:bg-surface-hover;
}

.option:focus-visible {
    @apply outline-none ring-2 ring-[color:var(--relationship-accent)];
}

.option--selected {
    @apply bg-[color:var(--relationship-accent-surface)] border-[color:var(--relationship-accent)];
}

.option--selected:hover {
    @apply bg-[color:var(--relationship-accent-surface)];
}

.option__label {
    @apply body-bold text-ink;
}

.option__body {
    @apply body-small text-ink-muted mt-1;
}

.option--selected .option__body {
    @apply text-ink;
}

.actions {
    @apply flex items-center justify-between pt-2;
}

.back-text {
    @apply body-small text-ink-muted hover:text-ink transition-colors;
}
</style>
